import { useId, useState, type ReactNode } from 'react';
import { Chip } from '@nyce/admin-kit/ui';
import { TabPanel } from '@nyce/admin-kit/console';
import { Kpi, TableScroller, Tabs } from '../../components/common';
import { formatBytes, formatCount, formatDateTime, formatDuration } from '../../lib/format';
import './statistics.css';

/*
 * The database part of a logserver diagnostics record. SQL Server and PostgreSQL installations send the same sections
 * (cpu, memory, disk, connections, indexes, expensiveQueries, unavailable) with the fields the engine has and null for
 * the rest, so the overview and the disk and query tables show whatever is filled in. Connections and the index section
 * mean different things per engine and get their own columns: PostgreSQL's "indexes" are tables read by sequential
 * scans (userSeeks = seq scans, indexAdvantage = rows they read), SQL Server's are the missing-index suggestions.
 */

type Row = Record<string, unknown>;
export type DbEngine = 'postgres' | 'sqlserver';

export interface DbDiagnostics {
  engine: DbEngine;
  name: string | null;
  cpu: Row[];
  memory: Row | null;
  disk: Row[];
  connections: Row[];
  indexes: Row[];
  queries: Row[];
  unavailable: string[];
}

const rows = (v: unknown): Row[] =>
  Array.isArray(v) ? v.filter((r): r is Row => !!r && typeof r === 'object' && !Array.isArray(r)) : [];
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const has = (list: Row[], key: string) => list.some((r) => r[key] != null);

/** The record's database part, or null when it is not one (then the drawer shows the JSON as it is). */
export function readDbDiagnostics(raw: unknown): DbDiagnostics | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const d = raw as Row;
  const sections = ['cpu', 'memory', 'disk', 'connections', 'indexes', 'expensiveQueries'];
  if (!sections.some((k) => Array.isArray(d[k]))) return null;
  const connections = rows(d.connections);
  const indexes = rows(d.indexes);
  const disk = rows(d.disk);
  const memory = rows(d.memory);
  const postgres =
    has(connections, 'pid') ||
    has(connections, 'usename') ||
    has(indexes, 'liveTuples') ||
    has(memory, 'blocksHit') ||
    has(disk, 'scope');
  const cpu = rows(d.cpu).sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')));
  return {
    engine: postgres ? 'postgres' : 'sqlserver',
    name: str(d.name),
    cpu,
    memory: memory[memory.length - 1] ?? null,
    disk,
    connections,
    indexes,
    queries: rows(d.expensiveQueries),
    unavailable: (Array.isArray(d.unavailable) ? d.unavailable : []).map((u) =>
      typeof u === 'string' ? u : JSON.stringify(u)
    ),
  };
}

/** SQL Server's `blkBy` (sp_who2): the blocking SPID, or `.` / blank when nothing blocks. */
const blockedBy = (r: Row): string | null => {
  const b = String(r.blkBy ?? '').trim();
  return b && b !== '.' ? b : null;
};

const STATE_ORDER = [
  'active',
  'idle in transaction',
  'idle in transaction (aborted)',
  'running',
  'runnable',
  'suspended',
];

/** Connections by state (PostgreSQL `state`, SQL Server `status`); a connection without one is a background process. */
export function connectionStates(list: Row[]): { state: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const c of list) {
    const s = str(c.state) ?? str(c.status)?.trim().toLowerCase() ?? 'background';
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const rank = (s: string) =>
    STATE_ORDER.includes(s) ? STATE_ORDER.indexOf(s) : s === 'background' ? 99 : 50;
  return [...counts]
    .map(([state, count]) => ({ state, count }))
    .sort((a, b) => rank(a.state) - rank(b.state));
}

/** Busy connections first (active, idle in transaction, blocked), then the most recent activity. */
export function sortConnections(list: Row[]): Row[] {
  const rank = (c: Row) => {
    if (blockedBy(c)) return 0;
    const s = (str(c.state) ?? str(c.status)?.trim().toLowerCase() ?? '').toLowerCase();
    const i = STATE_ORDER.indexOf(s);
    return i >= 0 ? i + 1 : s ? 50 : 99;
  };
  const last = (c: Row) => String(c.query_start ?? c.lastBatch ?? '');
  return [...list].sort((a, b) => rank(a) - rank(b) || last(b).localeCompare(last(a)));
}

// ---------- formatting ----------

const dash = '—';
const pct = (v: unknown) => (num(v) == null ? dash : `${Number(num(v)!.toFixed(num(v)! >= 10 ? 0 : 2))} %`);
const count = (v: unknown) => (num(v) == null ? dash : formatCount(num(v)));
const bytes = (v: unknown) => formatBytes(num(v));
const gb = (v: unknown) => (num(v) == null ? dash : `${num(v)!.toLocaleString()} GB`);
const ms = (v: unknown) => {
  const n = num(v);
  if (n == null) return dash;
  return n >= 1000 ? formatDuration(n) : `${Number(n.toFixed(n >= 100 ? 0 : 1))} ms`;
};
const plain = (v: unknown) => (num(v) != null ? num(v)!.toLocaleString() : (str(String(v ?? '')) ?? dash));
const when = (v: unknown) => (str(v) ? formatDateTime(v as string) : dash);
const days = (s: number) => (s >= 172800 ? `${Math.round(s / 86400)} days` : `${Math.round(s / 3600)} h`);

/** Long SQL as a one-line preview; the whole statement opens below it. */
function Sql({ text }: { text: unknown }) {
  const s = str(text);
  if (!s) return <>{dash}</>;
  const flat = s.replace(/\s+/g, ' ').trim();
  if (flat.length <= 120) return <code className="sx-sql">{flat}</code>;
  return (
    <details className="sx-sql-more">
      <summary>
        <code className="sx-sql">{flat.slice(0, 120)}…</code>
      </summary>
      <pre className="sx-pre">{s}</pre>
    </details>
  );
}

interface Column {
  label: string;
  /** The raw field(s) the column reads; the column is left out when none of the rows has a value. */
  keys: string[];
  cell: (r: Row) => ReactNode;
  num?: boolean;
}

function Table({
  label,
  list,
  columns,
  empty,
}: {
  label: string;
  list: Row[];
  columns: Column[];
  empty: string;
}) {
  const shown = columns.filter((c) => c.keys.some((k) => has(list, k)));
  if (!list.length || !shown.length) return <p className="nyk-ui-hint">{empty}</p>;
  return (
    <TableScroller>
      <table className="nyk-ui-table nyk-cards" aria-label={label}>
        <thead>
          <tr>
            {shown.map((c) => (
              <th key={c.label} className={c.num ? 'sx-num' : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((r, i) => (
            <tr key={i}>
              {shown.map((c, j) => (
                <td
                  key={c.label}
                  className={j === 0 ? 'nyk-card-title' : c.num ? 'sx-num' : undefined}
                  data-label={j === 0 ? undefined : c.label}
                >
                  {c.cell(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroller>
  );
}

// ---------- columns per section ----------

const DISK: Column[] = [
  { label: 'Name', keys: ['name', 'drive'], cell: (r) => str(r.name) ?? str(r.drive) ?? dash },
  {
    label: 'Kind',
    keys: ['scope', 'logicalVolumeName'],
    cell: (r) => str(r.scope) ?? str(r.logicalVolumeName) ?? dash,
  },
  { label: 'Location', keys: ['location'], cell: (r) => str(r.location) ?? dash },
  { label: 'File system', keys: ['fileSystem'], cell: (r) => str(r.fileSystem) ?? dash },
  {
    label: 'Size',
    keys: ['sizeBytes', 'sizeGb'],
    num: true,
    cell: (r) => (num(r.sizeBytes) != null ? bytes(r.sizeBytes) : gb(r.sizeGb)),
  },
  {
    label: 'Capacity',
    keys: ['capacityBytes', 'capacityGb'],
    num: true,
    cell: (r) => (num(r.capacityBytes) != null ? bytes(r.capacityBytes) : gb(r.capacityGb)),
  },
  {
    label: 'Free',
    keys: ['freeBytes', 'freeGb'],
    num: true,
    cell: (r) => (num(r.freeBytes) != null ? bytes(r.freeBytes) : gb(r.freeGb)),
  },
  { label: '% free', keys: ['freePct'], num: true, cell: (r) => pct(r.freePct) },
];

/** A value with a second, quieter line under it (folds two columns into one so the query keeps its room). */
const twoLines = (main: ReactNode, sub: string | null) => (
  <>
    {main}
    {sub && <div className="nyk-ui-hint">{sub}</div>}
  </>
);

const PG_CONNECTIONS: Column[] = [
  { label: 'PID', keys: ['pid'], cell: (r) => plain(r.pid) },
  { label: 'State', keys: ['state', 'pid'], cell: (r) => <StateChip state={str(r.state) ?? 'background'} /> },
  {
    label: 'User',
    keys: ['usename', 'application_name'],
    cell: (r) => twoLines(str(r.usename) ?? dash, str(r.application_name)),
  },
  {
    label: 'Client',
    keys: ['client_addr'],
    cell: (r) =>
      str(r.client_addr) ? `${r.client_addr}${num(r.client_port) != null ? `:${r.client_port}` : ''}` : dash,
  },
  {
    label: 'Last query at',
    keys: ['query_start', 'backend_start'],
    cell: (r) =>
      twoLines(when(r.query_start), str(r.backend_start) ? `connected ${when(r.backend_start)}` : null),
  },
  { label: 'Last query', keys: ['query'], cell: (r) => <Sql text={r.query} /> },
];

const SQL_CONNECTIONS: Column[] = [
  { label: 'SPID', keys: ['spid'], cell: (r) => plain(r.spid) },
  {
    label: 'Status',
    keys: ['status'],
    cell: (r) => <StateChip state={str(r.status)?.trim().toLowerCase() ?? dash} />,
  },
  { label: 'Login', keys: ['login'], cell: (r) => str(r.login)?.trim() ?? dash },
  { label: 'Blocked by', keys: ['blkBy'], cell: (r) => blockedBy(r) ?? dash },
  { label: 'Database', keys: ['dbName'], cell: (r) => str(r.dbName) ?? dash },
  { label: 'Command', keys: ['command'], cell: (r) => str(r.command)?.trim() ?? dash },
  { label: 'CPU', keys: ['cpuTime'], num: true, cell: (r) => plain(r.cpuTime) },
  { label: 'Disk I/O', keys: ['diskIO'], num: true, cell: (r) => plain(r.diskIO) },
  {
    label: 'Last batch',
    keys: ['lastBatch'],
    cell: (r) => (str(r.lastBatch)?.includes('T') ? when(r.lastBatch) : plain(r.lastBatch)),
  },
];

const PG_INDEXES: Column[] = [
  { label: 'Table', keys: ['table'], cell: (r) => str(r.table) ?? dash },
  { label: 'Seq scans', keys: ['userSeeks'], num: true, cell: (r) => count(r.userSeeks) },
  { label: 'Rows per scan', keys: ['avgTuplesPerScan'], num: true, cell: (r) => count(r.avgTuplesPerScan) },
  {
    label: 'Rows read by seq scans',
    keys: ['indexAdvantage'],
    num: true,
    cell: (r) => count(r.indexAdvantage),
  },
  { label: 'Index scans', keys: ['indexScans'], num: true, cell: (r) => count(r.indexScans) },
  { label: 'Live rows', keys: ['liveTuples'], num: true, cell: (r) => count(r.liveTuples) },
  { label: 'Table size', keys: ['tableBytes'], num: true, cell: (r) => bytes(r.tableBytes) },
  { label: 'Last seq scan', keys: ['lastUserSeek'], cell: (r) => when(r.lastUserSeek) },
];

const SQL_INDEXES: Column[] = [
  { label: 'Table', keys: ['table'], cell: (r) => str(r.table) ?? dash },
  { label: 'Advantage', keys: ['indexAdvantage'], num: true, cell: (r) => count(r.indexAdvantage) },
  { label: 'Equality columns', keys: ['equalityColumns'], cell: (r) => str(r.equalityColumns) ?? dash },
  { label: 'Inequality columns', keys: ['inequalityColumns'], cell: (r) => str(r.inequalityColumns) ?? dash },
  { label: 'Included columns', keys: ['includedColumns'], cell: (r) => str(r.includedColumns) ?? dash },
  { label: 'Seeks', keys: ['userSeeks'], num: true, cell: (r) => count(r.userSeeks) },
  { label: 'Avg cost', keys: ['avgTotalUserCost'], num: true, cell: (r) => plain(r.avgTotalUserCost) },
  { label: 'Avg impact', keys: ['avgUserImpact'], num: true, cell: (r) => pct(r.avgUserImpact) },
  { label: 'Compiles', keys: ['uniqueCompiles'], num: true, cell: (r) => count(r.uniqueCompiles) },
  { label: 'Last seek', keys: ['lastUserSeek'], cell: (r) => when(r.lastUserSeek) },
];

/** PostgreSQL's durations are milliseconds (pg_stat_statements); SQL Server's are shown as the collector sends them. */
const queryColumns = (engine: DbEngine): Column[] => {
  const time = engine === 'postgres' ? ms : plain;
  return [
    { label: 'Statement', keys: ['statementText'], cell: (r) => <Sql text={r.statementText} /> },
    { label: 'Executions', keys: ['executionCount'], num: true, cell: (r) => count(r.executionCount) },
    { label: 'Avg duration', keys: ['avgDuration'], num: true, cell: (r) => time(r.avgDuration) },
    { label: 'Total duration', keys: ['totalDuration'], num: true, cell: (r) => time(r.totalDuration) },
    { label: 'Avg CPU', keys: ['avgCpu'], num: true, cell: (r) => plain(r.avgCpu) },
    {
      label: engine === 'postgres' ? 'Avg blocks read' : 'Avg reads',
      keys: ['avgReads'],
      num: true,
      cell: (r) => count(r.avgReads),
    },
    { label: 'Avg rows', keys: ['avgRows'], num: true, cell: (r) => count(r.avgRows) },
  ];
};

function StateChip({ state }: { state: string }) {
  const tone =
    state === 'active' || state === 'running' || state === 'runnable'
      ? 'nyk-tone-green'
      : state.startsWith('idle in transaction') || state === 'suspended'
        ? 'nyk-tone-amber'
        : undefined;
  return (
    <Chip size="sm" className={tone}>
      {state}
    </Chip>
  );
}

// ---------- the view ----------

function Overview({ db }: { db: DbDiagnostics }) {
  const cpu = db.cpu[db.cpu.length - 1] ?? {};
  const m = db.memory ?? {};
  const database = db.disk.find((d) => d.scope === 'database');
  const tiles: ReactNode[] = [];
  if (num(cpu.sqlCPU) != null)
    tiles.push(
      <Kpi
        key="sqlcpu"
        label="Database CPU"
        value={pct(cpu.sqlCPU)}
        sub={num(cpu.systemCPU) != null ? `system ${pct(cpu.systemCPU)}` : undefined}
        help="CPU used by the database server when the snapshot was taken."
      />
    );
  if (num(cpu.backends) != null)
    tiles.push(
      <Kpi
        key="backends"
        label="Connections"
        value={`${count(cpu.activeBackends)} active`}
        sub={`of ${count(cpu.backends)}${num(m.maxConnections) != null ? ` · max ${count(m.maxConnections)}` : ''}`}
        tone={num(cpu.blockedBackends) ? 'amber' : undefined}
        delta={num(cpu.blockedBackends) ? `${count(cpu.blockedBackends)} blocked` : undefined}
      />
    );
  if (num(m.cacheHitPercent) != null)
    tiles.push(
      <Kpi
        key="cache"
        label="Cache hit"
        value={pct(m.cacheHitPercent)}
        sub={num(m.blocksRead) != null ? `${count(m.blocksRead)} blocks from disk` : undefined}
        help="Share of block reads served from the buffer cache since the statistics were last reset."
      />
    );
  if (num(m.serverMemoryBytes) != null)
    tiles.push(
      <Kpi
        key="mem"
        label="Server memory"
        value={bytes(m.serverMemoryBytes)}
        sub={
          num(m.serverMemoryTargetBytes) != null ? `target ${bytes(m.serverMemoryTargetBytes)}` : undefined
        }
      />
    );
  if (num(m.totalPhysicalBytes) != null)
    tiles.push(
      <Kpi
        key="phys"
        label="Physical memory"
        value={bytes(m.totalPhysicalBytes)}
        sub={
          num(m.availablePhysicalBytes) != null ? `${bytes(m.availablePhysicalBytes)} available` : undefined
        }
      />
    );
  if (database && num(database.sizeBytes) != null)
    tiles.push(
      <Kpi
        key="size"
        label="Database size"
        value={bytes(database.sizeBytes)}
        sub={str(database.name) ?? undefined}
      />
    );
  if (num(cpu.transactionsCommitted) != null)
    tiles.push(
      <Kpi
        key="tx"
        label="Transactions"
        value={count(cpu.transactionsCommitted)}
        sub={`${count(cpu.transactionsRolledBack)} rolled back${num(cpu.windowSeconds) ? ` · over ${days(num(cpu.windowSeconds)!)}` : ''}`}
        help="Committed transactions since the statistics were last reset."
      />
    );
  const settings: [string, string][] = [
    ['Cores', plain(cpu.cores)],
    ['Logical processors', plain(cpu.logicalProcessors)],
    ['work_mem', num(m.workMemBytes) != null ? bytes(m.workMemBytes) : dash],
    [
      'maintenance_work_mem',
      num(m.maintenanceWorkMemBytes) != null ? bytes(m.maintenanceWorkMemBytes) : dash,
    ],
    ['File cache', num(m.fileCacheBytes) != null ? bytes(m.fileCacheBytes) : dash],
    [
      'Swap free',
      num(m.swapFreeBytes) != null ? `${bytes(m.swapFreeBytes)} of ${bytes(m.swapTotalBytes)}` : dash,
    ],
  ].filter(([, v]) => v !== dash) as [string, string][];
  return (
    <>
      {tiles.length > 0 ? (
        <div className="nyk-kpis" data-testid="db-kpis">
          {tiles}
        </div>
      ) : (
        <p className="nyk-ui-hint">No CPU or memory figures in this snapshot.</p>
      )}
      {settings.length > 0 && (
        <dl className="sx-meta">
          {settings.map(([k, v]) => (
            <span key={k}>
              {k} <b>{v}</b>
            </span>
          ))}
        </dl>
      )}
      {db.cpu.length > 1 && (
        <p className="nyk-ui-hint">
          CPU over {db.cpu.length} samples, {when(db.cpu[0].date)} – {when(cpu.date)}: database{' '}
          {pct(Math.max(...db.cpu.map((c) => num(c.sqlCPU) ?? 0)))} at most
          {has(db.cpu, 'systemCPU') &&
            `, system ${pct(Math.max(...db.cpu.map((c) => num(c.systemCPU) ?? 0)))} at most`}
          .
        </p>
      )}
    </>
  );
}

type Section = 'overview' | 'connections' | 'indexes' | 'queries' | 'disk';

/** The structured view of a diagnostics record's database part, or its JSON when the shape is not one we know. */
export function DatabaseDiagnostics({ raw }: { raw: unknown }) {
  const db = readDbDiagnostics(raw);
  const tabs = useId();
  const [section, setSection] = useState<Section>('overview');
  if (!db) return <pre className="sx-pre">{JSON.stringify(raw, null, 2)}</pre>;
  const pg = db.engine === 'postgres';
  const states = connectionStates(db.connections);
  return (
    <div data-testid="db-diagnostics" data-engine={db.engine}>
      <p className="nyk-ui-hint">
        {pg ? 'PostgreSQL' : 'SQL Server'}
        {db.name && (
          <>
            {' '}
            · <b>{db.name}</b>
          </>
        )}
      </p>
      {db.unavailable.length > 0 && (
        <p className="nyk-notice nyk-notice-warn" data-testid="db-unavailable">
          Not collected: {db.unavailable.join('; ')}
        </p>
      )}
      <Tabs
        id={tabs}
        value={section}
        onChange={setSection}
        aria-label="Database diagnostics"
        options={[
          { value: 'overview', label: 'Overview' },
          { value: 'connections', label: `Connections (${db.connections.length})` },
          { value: 'indexes', label: `${pg ? 'Seq scans' : 'Missing indexes'} (${db.indexes.length})` },
          { value: 'queries', label: `Queries (${db.queries.length})` },
          { value: 'disk', label: `Disk (${db.disk.length})` },
        ]}
      />
      <TabPanel tabs={tabs} value={section} className="sx-db-panel">
        {section === 'overview' && <Overview db={db} />}
        {section === 'connections' && (
          <>
            {states.length > 0 && (
              <p className="sx-db-states" data-testid="db-states">
                {states.map((s) => (
                  <span key={s.state}>
                    <StateChip state={s.state} /> {s.count}
                  </span>
                ))}
              </p>
            )}
            <Table
              label="Connections"
              list={sortConnections(db.connections)}
              columns={pg ? PG_CONNECTIONS : SQL_CONNECTIONS}
              empty="No connections in this snapshot."
            />
          </>
        )}
        {section === 'indexes' && (
          <>
            <p className="nyk-ui-hint">
              {pg
                ? 'Tables read most by sequential scans: candidates for an index. "Rows read by seq scans" = scans × rows per scan.'
                : 'Indexes SQL Server suggests from the queries it ran since it started, by advantage.'}
            </p>
            <Table
              label={pg ? 'Tables read by sequential scans' : 'Missing indexes'}
              list={db.indexes}
              columns={pg ? PG_INDEXES : SQL_INDEXES}
              empty="Nothing in this snapshot."
            />
          </>
        )}
        {section === 'queries' && (
          <Table
            label="Expensive queries"
            list={db.queries}
            columns={queryColumns(db.engine)}
            empty="No queries in this snapshot."
          />
        )}
        {section === 'disk' && (
          <Table label="Disk" list={db.disk} columns={DISK} empty="No disk figures in this snapshot." />
        )}
      </TabPanel>
      <details className="sx-db-raw">
        <summary>JSON</summary>
        <pre className="sx-pre">{JSON.stringify(raw, null, 2)}</pre>
      </details>
    </div>
  );
}
